/**
 * Gateway-HMAC bundle canonicalization trio + constant (D192).
 *
 * The edge gateway SIGNS a fixed set of forwarded headers; every
 * downstream service VERIFIES them. Signer and verifier MUST compute the
 * exact same canonical bytes or every request 401s — so the byte-shape
 * lives here, in one place, and both sides call the same code.
 *
 * Locked canonical form (D192 — byte-for-byte):
 *
 *   canonical_bytes = concat( lower(name_i) + ":" + value_i + "\n"
 *                             for i in 1..len(order) )
 *
 * - header NAMES ASCII-lowercased; header VALUES UTF-8 verbatim
 *   (NO trim, NO normalization).
 * - separator: single LF (0x0A); trailing LF after the last header.
 *   No CR, no CRLF.
 * - a name in `order` absent from `headers` is NOT permitted:
 *   `sign` throws, `verify` returns `INCOMPLETE_BUNDLE`.
 * - signature: base64url(HMAC-SHA-256(secret, canonical_bytes)),
 *   UNPADDED (RFC 4648 § 5 — no trailing `=`).
 *
 * STATELESS — no key fetching. The caller reads raw secret bytes from
 * Secrets Manager (`nodii/<env>/gateway/hmac_secret`) and passes them in.
 *
 * The header ORDER is NOT caller-owned. It used to be — this file's own
 * comment said so, and that sentence is why the 14-name list was hand-copied
 * into 10 repos (edge, auth, tenant, billing, hr, finance, ticketing, kyc,
 * notification, and obs's Go port). All 10 copies are byte-identical today,
 * which is the best case, not a safe one: the order is a byte-for-byte
 * agreement between one signer and every verifier, so a single copy drifting
 * does not degrade — it 401s every request to that service, with a MISMATCH
 * that names nothing. It has already happened once (billing sat on the stale
 * 13-name D353 list after D355 moved the fleet to 14; every gateway request
 * 401'd until the E2E harness caught it on 2026-06-17). The list now lives
 * HERE, next to the code that canonicalizes over it, and is the DEFAULT for
 * sign/verify/canonicalize — so the correct call passes no order at all.
 */

import { createHmac, timingSafeEqual } from "node:crypto";

/** Header carrying the gateway HMAC signature on the wire (D192). */
export const GATEWAY_HMAC_HEADER = "x-nodii-gateway-signature";

/**
 * The LOCKED canonical bundle order — D192 (byte-shape) + D353 (the 9 principal
 * headers, D350-claim order) + D355 (locked 2026-06-15, amends D353: 13 → 14,
 * inserting `x-nodii-tenant-subdomain` ADJACENT to `x-nodii-tenant-id` at
 * position 4).
 *
 * Signer (nodii-edge) and every verifier MUST canonicalize over this EXACT
 * order, or every request 401s MISMATCH. The signature header itself
 * ({@link GATEWAY_HMAC_HEADER}) carries the MAC and is NOT in the canonical
 * bytes. Edge stamps all 14 UNCONDITIONALLY (absent value == empty string), so
 * a verifier reads each by name with an empty-string default and still matches
 * the signer byte-for-byte.
 *
 * NOT in the signed set (edge forwards them unsigned): `tracestate` (W3C
 * diagnostics), and `x-nodii-pii-capabilities` — the D558 per-model masking
 * matrix is signed INDEPENDENTLY as a sibling precisely because adding a 15th
 * name here while downstreams reconstruct 14 would mismatch every request in
 * the fleet.
 *
 * CHANGING THIS LIST IS A FLEET-COORDINATED RE-LOCK, not a library change: the
 * signer and all verifiers must move in the same deploy window.
 */
export const GATEWAY_BUNDLE_ORDER: readonly string[] = [
  // 9 D353 principal-claim headers (D350-claim order) + x-nodii-tenant-subdomain
  // (D355 — a tenant-scoping signal, not a principal claim) adjacent to tenant-id.
  "x-nodii-sub",
  "x-nodii-principal-kind",
  "x-nodii-tenant-id",
  "x-nodii-tenant-subdomain",
  "x-nodii-membership-id",
  "x-nodii-permissions",
  "x-nodii-amr",
  "x-nodii-jti",
  "x-nodii-impersonator",
  "x-nodii-impersonation-session-id",
  // Operational headers (signed so a downstream can trust intent/correlation).
  "x-nodii-intent-id",
  "x-nodii-intent-type",
  "x-request-id",
  "traceparent",
] as const;

/** Default rotation grace window: 24h, per D192. */
export const GATEWAY_HMAC_ROTATION_GRACE_MS = 24 * 60 * 60 * 1000;

/** ASCII A-Z → a-z only. Header names are ASCII per RFC 7230; this
 * intentionally does NOT touch non-ASCII bytes (matches Python's
 * `str.encode("ascii")` lowercasing of names and Go's ASCII fold). */
function asciiLower(name: string): string {
  let out = "";
  for (let i = 0; i < name.length; i++) {
    const c = name.charCodeAt(i);
    out += c >= 0x41 && c <= 0x5a ? String.fromCharCode(c + 0x20) : name[i];
  }
  return out;
}

/**
 * Thrown by {@link signGatewayBundle} when a name in `order` is absent
 * from `headers`. The verifier instead returns `INCOMPLETE_BUNDLE` (it
 * must not throw on attacker-controlled input).
 */
export class GatewayBundleIncompleteError extends Error {
  /** The first `order` name that had no matching `headers` entry. */
  readonly missingHeader: string;
  constructor(missingHeader: string) {
    super(`gateway HMAC bundle incomplete: missing header "${missingHeader}"`);
    this.name = "GatewayBundleIncompleteError";
    this.missingHeader = missingHeader;
  }
}

export interface CanonicalizeGatewayBundleOptions {
  /** All request headers (only those named in `order` are consumed). */
  headers: Record<string, string>;
  /**
   * Ordered header names to include. Defaults to {@link GATEWAY_BUNDLE_ORDER},
   * the fleet-locked 14. Pass an explicit list ONLY to reproduce a historical
   * order in a test — a production caller that passes its own list has
   * re-created the drift hazard this default exists to remove.
   */
  order?: readonly string[];
}

/**
 * Produce the raw canonical bytes that go into the HMAC. Exposed for
 * tests + verifier debugging.
 *
 * @throws {GatewayBundleIncompleteError} if any `order` name is absent
 *   from `headers`.
 */
export function canonicalizeGatewayBundle(
  opts: CanonicalizeGatewayBundleOptions,
): Uint8Array {
  const { headers } = opts;
  const order = opts.order ?? GATEWAY_BUNDLE_ORDER;
  const parts: string[] = [];
  for (const name of order) {
    if (!Object.prototype.hasOwnProperty.call(headers, name)) {
      throw new GatewayBundleIncompleteError(name);
    }
    const value = headers[name];
    if (value === undefined) {
      throw new GatewayBundleIncompleteError(name);
    }
    parts.push(`${asciiLower(name)}:${value}\n`);
  }
  return new TextEncoder().encode(parts.join(""));
}

/** base64url-encode raw bytes, UNPADDED (RFC 4648 § 5). */
function base64urlEncode(bytes: Uint8Array): string {
  return Buffer.from(bytes)
    .toString("base64")
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** Strict base64url-unpadded decode. Returns `null` for any string that
 * is not canonical unpadded base64url (padded `=`, `+`/`/`, whitespace,
 * or otherwise malformed). */
function base64urlDecodeStrict(s: string): Uint8Array | null {
  // Reject padding and the standard-base64 alphabet outright.
  if (!/^[A-Za-z0-9_-]*$/.test(s)) return null;
  if (s.length % 4 === 1) return null; // impossible base64 length
  const buf = Buffer.from(s, "base64url");
  // Round-trip: re-encode and require an exact byte match. This rejects
  // non-canonical inputs (trailing bits set, padded variants) that
  // `Buffer.from` would otherwise accept silently.
  if (base64urlEncode(buf) !== s) return null;
  return new Uint8Array(buf);
}

function hmacSha256(secret: Uint8Array, message: Uint8Array): Uint8Array {
  const h = createHmac("sha256", Buffer.from(secret));
  h.update(Buffer.from(message));
  return new Uint8Array(h.digest());
}

export interface SignGatewayBundleOptions {
  headers: Record<string, string>;
  /** Defaults to {@link GATEWAY_BUNDLE_ORDER}. */
  order?: readonly string[];
  /** Raw secret bytes (caller reads from Secrets Manager). */
  secret: Uint8Array;
}

/**
 * Sign the canonical bundle: `base64url(HMAC-SHA-256(secret, canonical))`,
 * UNPADDED.
 *
 * @throws {GatewayBundleIncompleteError} if any `order` name is absent.
 */
export function signGatewayBundle(opts: SignGatewayBundleOptions): string {
  const canonical = canonicalizeGatewayBundle({
    headers: opts.headers,
    order: opts.order,
  });
  return base64urlEncode(hmacSha256(opts.secret, canonical));
}

export interface GatewayHmacSecrets {
  /** Active secret. Edge always signs with this. */
  current: Uint8Array;
  /** Prior secret (the value `current` held before the last rotation). */
  previous?: Uint8Array | null;
  /** ISO-8601 timestamp of the last rotation; `null`/absent if never. */
  rotatedAt?: string | null;
}

export interface VerifyGatewayBundleOptions {
  headers: Record<string, string>;
  /** Defaults to {@link GATEWAY_BUNDLE_ORDER}. */
  order?: readonly string[];
  signature: string;
  secrets: GatewayHmacSecrets;
  /** Rotation grace window in ms. Default 24h. */
  rotationGraceMs?: number;
}

export type VerifyGatewayBundleResult =
  | { ok: true; used: "current" | "previous" }
  | {
      ok: false;
      reason:
        | "MISMATCH"
        | "INCOMPLETE_BUNDLE"
        | "OUT_OF_ROTATION_GRACE"
        | "BAD_BASE64URL";
    };

/** Constant-time equality over two byte arrays of (possibly) unequal len. */
function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  return timingSafeEqual(Buffer.from(a), Buffer.from(b));
}

/**
 * Verify a gateway HMAC bundle against `current`, falling back to
 * `previous` within the rotation grace window.
 *
 * Never throws on attacker-controlled input; all failure modes are
 * returned as `{ ok: false, reason }`:
 * - `INCOMPLETE_BUNDLE` — a name in `order` is absent from `headers`.
 * - `BAD_BASE64URL` — `signature` is not valid unpadded base64url.
 * - `MISMATCH` — neither eligible secret reproduces the signature.
 * - `OUT_OF_ROTATION_GRACE` — `current` mismatched and the only other
 *   candidate (`previous`) is past the grace window (or `rotatedAt` is
 *   unusable), so `previous` was not tried.
 */
export function verifyGatewayBundle(
  opts: VerifyGatewayBundleOptions,
): VerifyGatewayBundleResult {
  const rotationGraceMs =
    opts.rotationGraceMs ?? GATEWAY_HMAC_ROTATION_GRACE_MS;

  let canonical: Uint8Array;
  try {
    canonical = canonicalizeGatewayBundle({
      headers: opts.headers,
      order: opts.order,
    });
  } catch (e) {
    if (e instanceof GatewayBundleIncompleteError) {
      return { ok: false, reason: "INCOMPLETE_BUNDLE" };
    }
    throw e;
  }

  const provided = base64urlDecodeStrict(opts.signature);
  if (provided === null) {
    return { ok: false, reason: "BAD_BASE64URL" };
  }

  // 1. Try current.
  const currentMac = hmacSha256(opts.secrets.current, canonical);
  if (bytesEqual(currentMac, provided)) {
    return { ok: true, used: "current" };
  }

  // 2. Try previous, but only within the rotation grace window.
  const previous = opts.secrets.previous;
  if (previous != null) {
    const withinGrace = isWithinRotationGrace(
      opts.secrets.rotatedAt ?? null,
      rotationGraceMs,
    );
    if (!withinGrace) {
      return { ok: false, reason: "OUT_OF_ROTATION_GRACE" };
    }
    const previousMac = hmacSha256(previous, canonical);
    if (bytesEqual(previousMac, provided)) {
      return { ok: true, used: "previous" };
    }
  }

  return { ok: false, reason: "MISMATCH" };
}

/** `true` iff `rotatedAt` parses and `(now - rotatedAt) <= graceMs`.
 * A null/unparseable `rotatedAt` is treated as out-of-grace (we cannot
 * prove the previous secret is still in its window). */
function isWithinRotationGrace(
  rotatedAt: string | null,
  graceMs: number,
): boolean {
  if (rotatedAt == null) return false;
  const t = Date.parse(rotatedAt);
  if (Number.isNaN(t)) return false;
  const age = Date.now() - t;
  // Future timestamps (age < 0) are within grace; clock skew shouldn't
  // reject an otherwise-valid previous secret.
  return age <= graceMs;
}
